import type { ProtectedRouteOptions } from "../../routing-services";
import { ExtendedUserInfo, useAuthentication } from "../authentication";
import { configureRouter } from "@/.shared/services/routing-services";
import NotAuthorizedView from "@/.shared/views/NotAuthorizedView.vue";
import { useIntl } from "../../i18n";
import type { RouteLocationNormalized } from "vue-router";


export interface AuthorizationService {
    getUserPermissions(): Iterable<string>;
    hasPermission(permission: string): boolean;
    getUserGrupo(): string | null;
    isGrupo(grupo: string): boolean;
}

export interface AuthorizationServiceConfiguration {
    commonRoutePermissionChecker?: (userInfo: ExtendedUserInfo, route: RouteLocationNormalized) => boolean;
}

export class AuthorizationServiceImpl implements AuthorizationService {
    private _auth: ReturnType<typeof useAuthentication>;

    constructor(cfg: AuthorizationServiceConfiguration) {
        this._auth = useAuthentication();
    }

    public *getUserPermissions(): Iterable<string> {
        for (const permission of this.userInfo.permissions ?? []) {
            yield permission;
        }
    }

    public hasPermission(permission: string) {
        return this.userInfo.permissions?.has(permission) ?? false;
    }

    public getUserGrupo(): string | null {
        return this.userInfo.grupoUsuario;
    }

    public isGrupo(grupo: string): boolean {
        return this.userInfo.grupoUsuario === grupo;
    }

    get userInfo() : ExtendedUserInfo {
        return this._auth.userInfo as ExtendedUserInfo;
    }
}

let instance: AuthorizationServiceImpl;



export function configureAuthorization(cfg: AuthorizationServiceConfiguration) {
    instance = new AuthorizationServiceImpl(cfg);
    configureRouter(router => {

        router.addRoute("not-authorized", b => b.withPath("not-authorized").withComponent(NotAuthorizedView).allowAnonymous());

        router.beforeEach(async (to, from, next) => {
            const prot = to.meta.protection as ProtectedRouteOptions;
            let destination = to.fullPath
            const isRedirecting = to.query.__firstRedirect === "true";

            const authentication = useAuthentication();
            const check = (resolve: (...args: any[]) => void) => {
                if (authentication.isInititalized) {
                    resolve();
                }
                else {
                    setTimeout(() => check(resolve), 50);
                }
            }
           
            await new Promise(resolve => check(resolve));
            
            if (isRedirecting) {
                const url = new window.URL("https://dummy" + to.fullPath);
                url.searchParams.delete("__firstRedirect");
                destination = `${url.pathname}${url.search}${url.hash}`;
            }

            if (prot) {
           
                //Verificamos si el usuario está logueado y gestionamos redirecciones
                let canHaveAccess = authentication.isLoggedIn();
                let isInitialLoad = from.matched.length == 0;

                if (!canHaveAccess) {
                    if (isInitialLoad && !isRedirecting) {
                        const url = new window.URL("https://dummy" + to.fullPath);
                        url.searchParams.append("__firstRedirect", "true");
                        authentication.login(`${url.pathname}${url.search}${url.hash}`);
                    }
                    else
                        return next(useIntl().i18Route({ name: "home", query: { redirectTo: destination } }));
                }

                //Aplicamos políticas comunes
                if (canHaveAccess && cfg.commonRoutePermissionChecker) {
                    canHaveAccess = cfg.commonRoutePermissionChecker(instance.userInfo, to);
                }

                //Aplicamos políticas específicas de la ruta
                if (canHaveAccess) {
                    if (typeof prot.authReqs === "function") {
                        canHaveAccess = prot.authReqs(instance.userInfo, to);
                    }
                    else if (Array.isArray(prot.authReqs)) {
                        for (const permission of prot.authReqs) {
                            if (!instance.hasPermission(permission)) {
                                canHaveAccess = false;
                                break;
                            }
                        }
                    }
                }

                if (!canHaveAccess) {
                    return next(useIntl().i18Route({ name: "not-authorized", query: { redirectTo: "/" } }));
                }
            }

            if (destination !== to.fullPath)
                next(destination);
            else
                next();
        });
    })

}

export function useAuthorization(): AuthorizationService {
    return instance;
}

export enum PermissionsApp {
    Administracion = 'ADMINISTRACION',
    EditAllUsuarios = 'EDIT_ALL_USUARIOS',
    EditSameCompanyAgentes = 'EDIT_SAME_COMPANY_AGENTES',
    EditSelfUsuario = 'EDIT_SELF_USUARIO',
    GetAgentes = 'GET_AGENTES',
    GetEmpresasAgentes = 'GET_EMPRESAS_AGENTES',
    GetRoles = 'GET_ROLES',
    ViewAllFacturas = 'VIEW_ALL_FACTURAS',
    ViewSameCompanyFacturas = 'VIEW_SAME_COMPANY_FACTURAS',
    CreateFacturas = 'CREATE_FACTURAS',
    EditEmpresaAgente = 'EDIT_EMPRESA_AGENTE',
    EditDatosFiscalesEmpresaAgente = 'EDIT_DATOS_FISCALES_EMPRESA_AGENTE',
    EditDatosPersonalesAgente = 'EDIT_PERSONAL_INFO_AGENTE',
    EditSelfEmpresaAgente = 'EDIT_SELF_EMPRESA_AGENTE',
    EditAllOperacionesEnergia = 'EDIT_ALL_OPERACIONES_ENERGIA',
    EditSameCompanyOperacionesEnergia = 'EDIT_SAME_COMPANY_OPERACIONES_ENERGIA',
    EditSelfOperacionesEnergia = 'EDIT_SELF_OPERACIONES_ENERGIA',
    EditEmpresaServicios = 'EDIT_EMPRESA_SERVICIOS',
    EditOfertaEnergia = 'EDIT_OFERTA_ENERGIA',
    EditServicioEnergia = 'EDIT_SERVICIO_ENERGIA',
    EditComisionEnergia = 'EDIT_COMISION_ENERGIA',
    EditRappelEnergia = 'EDIT_RAPPEL_ENERGIA',
    ViewAllClientes = 'VIEW_ALL_CLIENTES',
    EditSameCompanyClientes = 'EDIT_SAME_COMPANY_CLIENTES',
    EditSelfClientes = 'EDIT_SELF_CLIENTES',
    ViewSameCompanyLiquidaciones = "VIEW_SAME_COMPANY_LIQUIDACIONES",
    ViewSameCompanyRappelsMensuales = "VIEW_SAME_COMPANY_RAPPELS_MENSUALES",
    GetSameCompanyTickets = "GET_SAME_COMPANY_TICKETS",
    GetSelfTickets = "GET_SELF_TICKETS",
    GetAllTickets = "GET_ALL_TICKETS",
    EditSelfEmpresaInmobiliaria = 'EDIT_SELF_EMPRESA_INMOBILIARIA',
    EditSelfDelegacionInmobiliaria = 'EDIT_SELF_DELEGACION_INMOBILIARIA',
    EditSelfComparacionEnergia = 'EDIT_SELF_COMPARACION_ENERGIA',
    EditSameInmobiliariaAgente = 'EDIT_SAME_INMOBILIARIA_AGENTE',
    EditSameDelegacionAgente = 'EDIT_SAME_DELEGACION_AGENTE',
    EditSameCompanyDelegacion = 'EDIT_SAME_COMPANY_DELEGACION',
    GetSameDelegacionTickets = 'GET_SAME_DELEGACION_TICKETS',
    ViewDatosEconomicosOperaciones = 'VIEW_DATOS_ECONOMICOS_OPERACIONES',
    ViewSameCompanyOperaciones = 'VIEW_SAME_COMPANY_OPERACIONES_ENERGIA',
    ViewSameDelegacionOperaciones = 'VIEW_SAME_DELEGACION_OPERACIONES_ENERGIA',
    ViewSameCompanyClientesOperaciones = 'VIEW_SAME_COMPANY_CLIENTES_OPERACIONES_ENERGIA',
    EditSameRealStateCompanyInmueble = 'EDIT_SAME_REAL_STATE_COMPANY_INMUEBLE',
    EditSameRealStateAgencyInmueble = 'EDIT_SAME_REAL_STATE_AGENCY_INMUEBLE',
    EditAllEmpresaInmobiliaria = 'EDIT_ALL_EMPRESA_INMOBILIARIA',
    EditAllDelegacionInmobiliaria = 'EDIT_ALL_DELEGACION_INMOBILIARIA',
    EditAssociatedOperacionesEnergia = 'EDIT_ASSOCIATED_OPERACIONES_ENERGIA',
    ViewAssociatedFacturas = 'VIEW_ASSOCIATED_FACTURAS',
    ChangeStateAssociatedFacturas = 'CHANGE_STATE_ASSOCIATED_FACTURAS',
    GetAssociatedTickets = 'GET_ASSOCIATED_TICKETS',
    WriteMessageAssociatedTickets = 'WRITE_MESSAGE_ASSOCIATED_TICKETS',
    AskCloseAssociatedTicket = 'ASK_CLOSE_ASSOCIATED_TICKET',
    EditAllMarcoComisiones = 'EDIT_ALL_MARCO_COMISIONES',
    ViewAllBorradoresOperacion = 'VIEW_ALL_BORRADORES_OPERACION',
    EditComparacionesAgente = 'EDIT_COMPARACIONES_AGENTE',
    ViewNuevaVenta = 'VIEW_NUEVA_VENTA',
    ViewNuevaVentaInmobiliaria = 'VIEW_NUEVA_VENTA_INMOBILIARIA',
    ViewSameCompanyTramites = 'VIEW_SAME_COMPANY_TRAMITES',
    ViewRelatedCompanyBranchTramites = 'VIEW_RELATED_COMPANY_BRANCH_TRAMITES',
    ViewOperaciones = 'VIEW_OPERACIONES',
    ViewContactosLead = 'VIEW_CONTACTOS_LEAD',
    ViewClientesBoSection = 'VIEW_CLIENTES_BO_SECTION',
    ViewAllTramites = 'VIEW_ALL_TRAMITES',
    ViewBorradoresBoSection = 'VIEW_BORRADORES_BO_SECTION',
    ImportOperacionesEnergia = 'IMPORT_OPERACIONES_ENERGIA',
    SearchOfertasEnergia = 'SEARCH_OFERTAS_ENERGIA',
    SearchRetribucionesEnergia = 'SEARCH_RETRIBUCIONES_ENERGIA',
    EditAllComunicados = 'EDIT_ALL_COMUNICADOS',
    ViewComunicados = 'VIEW_COMUNICADO',
  }
    